import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Box, Flex, Link, Show, Text, Tooltip } from "@chakra-ui/react";

import { TextPrimary, TextSecondary, AuditsContainer } from "theme";
import { AiOutlineInfoCircle } from "react-icons/ai";

// assets
import { VerifiedUser } from "icons";

// components
import Card from "@components/Card";
import AuditsModal from "@components/AuditsModal";
import Icon from "@components/Icon";

import { StrapiCenterHeaderData } from "@customtypes/global";
import { formatNumber } from "@utils/globalCommon";

import usePageData from "@hooks/usePageData";

import styles from "./styles.module.scss";
import { REWARDSCALCULATORMAP } from "@constants/constants";
import { useDappSDKProvider } from "dapp-sdk-v2";
import useIsMobile from "@hooks/useMobile";
import { MobileNotSupported } from "@components/MobileNotSupported";
import { toFixedWithoutRounding } from "@utils/common";
import { useEmbed } from "dapp-sdk-v2/hooks/useEmbed";
import { SD } from "dapp-sdk-v2/constants/constants";

const TOKEN = process.env.NEXT_PUBLIC_TOKEN || "";

interface CardHeader {
  activeTab: string;
  title: string;
  currentPrice?: number;
  subTitle?: string;
}

const getSDUtilized = () => {
  return axios.get("https://universe.staderlabs.com/sd/SDUtilized");
};

const CenterHeader = ({ activeTab, subTitle, title }: CardHeader) => {
  const { isSupported, isMobile } = useIsMobile();
  const { isEmbed } = useEmbed();

  const [isAuditsModalOpen, setIsAuditsModalOpen] = useState(false);
  const [sdUtilized, setSDUtilized] = useState<number | null>(null);
  const { tvlData, apyData, conversionData } = useDappSDKProvider();

  const apy =
    apyData?.apy && apyData.apy > 0
      ? Number(Number(apyData.apy).toFixed(2))
      : 0;

  const baseAPY =
    apyData?.baseAPY && apyData.baseAPY > 0
      ? Number(Number(apyData.baseAPY).toFixed(2))
      : 0;

  const boostedAPY =
    apyData?.boostedAPY && apyData.boostedAPY > 0
      ? Number(Number(apyData.boostedAPY).toFixed(2))
      : null;

  const values = usePageData("$.page.data.center_header[0].values")[0];
  const audits = usePageData("$.page.data.audits")[0];
  const { action_cta, action_link } = audits || {};
  const buhBounty = usePageData("$.page.data.bug_bounty")[0];
  const { erc20_token = TOKEN } = usePageData("$.page.data")[0];

  const getSDUtilizedValue = async () => {
    const response = await getSDUtilized();
    setSDUtilized(response?.data?.value || null);
  };

  useEffect(() => {
    if (TOKEN === SD) {
      getSDUtilizedValue();
    }
  }, []);

  const getSDTokenTooltip = useCallback(
    (boosted_apy_cms?: string) => {
      return (
        <Tooltip
          placement="top"
          p="8px"
          borderRadius="8px"
          w="220px"
          pointerEvents="all"
          label={
            <Flex flexDir="column" gap="8px" color="textSecondary">
              {/* <Text fontWeight="500">{tooltipText}</Text> */}
              <Flex flexDir="column" gap="4px">
                <Flex justifyContent="space-between">
                  <Text>Base APR</Text>
                  <Text>{baseAPY ? `${baseAPY}%` : "0%"}</Text>
                </Flex>
                <Flex justifyContent="space-between">
                  <Text>Boosted APR</Text>
                  <Text>
                    {boostedAPY
                      ? `${boostedAPY}%`
                      : boosted_apy_cms
                      ? `${boosted_apy_cms}%`
                      : "0%"}
                  </Text>
                </Flex>
                <Flex justifyContent="space-between">
                  <Link
                    href="https://www.staderlabs.com/docs-v1/SD%20Utility%20Pool/SD%20Delegation/sd-delegation"
                    isExternal
                    // display="inline-block"
                    textDecor="none !important"
                    cursor="pointer"
                  >
                    <Text color="primary">Learn More</Text>
                  </Link>
                </Flex>
              </Flex>
            </Flex>
          }
          closeDelay={2000}
          bg="tooltipBackground"
        >
          <Text ml="4px" color="textSecondary" fontSize="14px">
            <AiOutlineInfoCircle />
          </Text>
        </Tooltip>
      );
    },
    [baseAPY, boostedAPY]
  );

  const getTooltip = useCallback(
    (section: StrapiCenterHeaderData, isAPY?: boolean) => {
      let tooltipText, tooltipCtaText, tooltipCtaLink;
      const {
        tooltip_cta_link,
        tooltip_cta_text,
        tooltip_text,
        ledger_tooltip_cta_link,
        ledger_tooltip_cta_text,
        ledger_tooltip_text,
        boosted_apy,
      } = section;
      tooltipText =
        isEmbed && ledger_tooltip_text ? ledger_tooltip_text : tooltip_text;
      tooltipCtaLink =
        isEmbed && ledger_tooltip_cta_link
          ? ledger_tooltip_cta_link
          : tooltip_cta_link;
      tooltipCtaText =
        isEmbed && ledger_tooltip_cta_text
          ? ledger_tooltip_cta_text
          : tooltip_cta_text;

      if (tooltipText && isAPY && TOKEN === SD) {
        return getSDTokenTooltip(boosted_apy);
      }

      return (
        <>
          {tooltipText && (
            <Tooltip
              placement="top"
              p="8px"
              borderRadius="8px"
              maxW="260px"
              label={
                <Box pointerEvents="all">
                  <Text color="textSecondary" fontWeight="500">
                    {tooltipText}
                    {tooltipCtaText && (
                      <Text
                        as="a"
                        href={tooltipCtaLink || ""}
                        target="_blank"
                        rel="noreferrer"
                        color="primary"
                        cursor="pointer"
                      >
                        {tooltipCtaText}
                      </Text>
                    )}
                  </Text>
                </Box>
              }
              closeDelay={2000}
              bg="tooltipBackground"
            >
              <Text ml="4px" color="textSecondary" fontSize="14px">
                <AiOutlineInfoCircle />
              </Text>
            </Tooltip>
          )}
        </>
      );
    },
    [getSDTokenTooltip, isEmbed]
  );

  const getSectionTitle = useCallback(
    (section: StrapiCenterHeaderData, isAPY?: boolean) => {
      return (
        <Flex alignItems="center">
          <TextSecondary>{section.title}</TextSecondary>

          {getTooltip(section, isAPY)}
        </Flex>
      );
    },
    [getTooltip]
  );

  const getAPYSection = useCallback(
    (section: StrapiCenterHeaderData) => {
      const { ledger_apy_add_on_perc, boosted_apy: boosted_apy_cms } = section;

      const extraAPY = boostedAPY ?? boosted_apy_cms ?? null;

      const finalApy = boostedAPY
        ? apy
        : extraAPY
        ? apy + Number(extraAPY)
        : apy;

      return (
        <Flex justifyContent="space-between">
          {getSectionTitle(section, true)}
          <TextPrimary
            fontWeight="600"
            lineHeight="24px"
            fontSize={{ base: "12px", lg: "16px" }}
          >
            {isEmbed && ledger_apy_add_on_perc
              ? `${ledger_apy_add_on_perc}%`
              : `${finalApy}%`}
          </TextPrimary>
        </Flex>
      );
    },
    [apy, getSectionTitle, isEmbed, boostedAPY]
  );

  const getTotalStakedSection = useCallback(
    (section: StrapiCenterHeaderData) => {
      return (
        <Flex justifyContent="space-between">
          {getSectionTitle(section)}
          <Flex alignItems="center">
            {tvlData?.tvl ? (
              <>
                <TextPrimary
                  fontWeight="600"
                  lineHeight="24px"
                  fontSize={{ base: "12px", lg: "16px" }}
                >
                  {`${formatNumber(tvlData.tvl.value)} ${erc20_token}`}
                </TextPrimary>
                <TextSecondary ml="6px">|</TextSecondary>
                <TextSecondary
                  ml="6px"
                  fontWeight={{ base: "400", lg: "600" }}
                  lineHeight="20px"
                  fontSize={{ base: "10px", lg: "14px" }}
                >
                  ${formatNumber(tvlData.tvl.usdTvl)}
                </TextSecondary>
              </>
            ) : (
              "-"
            )}
          </Flex>
        </Flex>
      );
    },
    [getSectionTitle, tvlData?.tvl]
  );

  const getSDUtilizedSection = useCallback(
    (section: StrapiCenterHeaderData) => {
      return (
        <Flex justifyContent="space-between">
          {getSectionTitle(section)}
          <Flex alignItems="center">
            {sdUtilized ? (
              <>
                <TextPrimary
                  fontWeight="600"
                  lineHeight="24px"
                  fontSize={{ base: "12px", lg: "16px" }}
                >
                  {`${formatNumber(sdUtilized)} ${erc20_token}`}
                </TextPrimary>
                {tvlData?.tvl?.usdPrice && (
                  <>
                    <TextSecondary ml="6px">|</TextSecondary>
                    <TextSecondary
                      ml="6px"
                      fontWeight={{ base: "400", lg: "600" }}
                      lineHeight="20px"
                      fontSize={{ base: "10px", lg: "14px" }}
                    >
                      ${formatNumber(sdUtilized * tvlData?.tvl?.usdPrice)}
                    </TextSecondary>
                  </>
                )}
              </>
            ) : (
              "-"
            )}
          </Flex>
        </Flex>
      );
    },
    [getSectionTitle, sdUtilized, tvlData?.usdPrice]
  );

  const getExchangeRateSection = useCallback(
    (section: StrapiCenterHeaderData) => {
      return (
        <Flex justifyContent="space-between">
          {getSectionTitle(section)}
          <Flex alignItems="center">
            {conversionData?.exchangeRate ? (
              <TextPrimary
                fontWeight="600"
                lineHeight="24px"
                fontSize={{ base: "12px", lg: "16px" }}
              >
                {toFixedWithoutRounding(`${conversionData?.exchangeRate}`, 6)}
              </TextPrimary>
            ) : (
              "-"
            )}
          </Flex>
        </Flex>
      );
    },
    [getSectionTitle, conversionData?.exchangeRate]
  );

  const getCenterHeaderItem = (section: StrapiCenterHeaderData) => {
    switch (section.key) {
      case "apy":
        return getAPYSection(section);
      case "totalStakedValue":
        return getTotalStakedSection(section);
      case "exchange_rate":
        return getExchangeRateSection(section);
      case "totalSDUtilized":
        return getSDUtilizedSection(section);
    }
  };

  return (
    <Box width="100%">
      <Flex
        justifyContent="space-between"
        alignItems={{ base: "flex-start", xl: "center" }}
        direction="column"
      >
        {!isSupported && isMobile ? null : (
          <Flex
            alignItems="flex-start"
            direction="column"
            justifyContent="center"
            textAlign="center"
            width="100%"
          >
            <Flex alignItems="center">
              <TextPrimary
                fontWeight="600"
                fontSize={{ base: "24px", md: "32px" }}
                lineHeight={{ base: "28px", md: "38px" }}
              >
                {title
                  ? title
                  : `${activeTab || ""} ${REWARDSCALCULATORMAP[TOKEN]}`}
              </TextPrimary>
              {audits?.title ? (
                <AuditsContainer
                  onClick={() => setIsAuditsModalOpen(true)}
                  ml="8px"
                >
                  <Icon
                    height="16px"
                    width="16px"
                    Icon={VerifiedUser}
                    color="#38A169"
                  />
                  <Show above="sm">
                    <TextPrimary
                      fontWeight="400"
                      fontSize="14px"
                      lineHeight="20px"
                    >
                      {audits?.title}
                    </TextPrimary>
                  </Show>
                </AuditsContainer>
              ) : null}
            </Flex>
            {subTitle && (
              <Box
                alignItems="center"
                fontWeight="400"
                fontSize={{ base: "12px", md: "14px" }}
                lineHeight={{ base: "18px", md: "20px" }}
                textAlign="left"
                mt="4px"
              >
                <TextSecondary display="inline">{subTitle}</TextSecondary>
                {TOKEN === SD && (
                  <Link
                    href="https://www.staderlabs.com/docs-v1/category/sd-utility-pool"
                    isExternal
                    pl="4px"
                    display="inline-block"
                    textDecor="none !important"
                  >
                    <Text
                      color="primary"
                      borderBottom="1px solid"
                      borderColor="primary"
                    >
                      Learn More
                    </Text>
                  </Link>
                )}
              </Box>
            )}
          </Flex>
        )}

        {values?.length ? (
          <Card
            p={{ base: "1.5rem 1rem", md: "1.5rem 2rem" }}
            bg="backgroundSecondary"
            w="100%"
            mt={{ base: "1rem", md: "2rem" }}
            fontSize={{ base: "12px", md: "14px" }}
            fontWeight={{ base: "400", md: "600" }}
            borderRadius="1rem"
            className={styles.centerHeader}
          >
            {values.map((data: StrapiCenterHeaderData) => (
              <div key={data.key} className={styles.centerHeaderItem}>
                {getCenterHeaderItem(data)}
              </div>
            ))}
          </Card>
        ) : null}
      </Flex>

      {!isSupported && isMobile && <MobileNotSupported />}

      <AuditsModal
        isOpen={isAuditsModalOpen}
        closeModal={() => setIsAuditsModalOpen(false)}
        title={audits?.title}
        subTitle={audits?.sub_title}
        auditsData={audits?.audits_data}
        bugBountySection={buhBounty}
        actionCTA={action_cta}
        actionLink={action_link}
      />
    </Box>
  );
};

export default CenterHeader;
